import { Button, Tabs } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { BulkResolutionResult, MatchSuggestion } from '../../types';
import useConfirmMatches from '../hooks/useConfirmMatches';
import useRejectMatches from '../hooks/useRejectMatches';
import useReopenMatches from '../hooks/useReopenMatches';
import useUnlinkMatches from '../hooks/useUnlinkMatches';
import AmbiguousGroupCards from './AmbiguousGroupCards';
import SuggestionReviewTable from './SuggestionReviewTable';

type MatchingSectionNavProps = {
  suggestions: MatchSuggestion[];
};

type Action = 'confirmed' | 'rejected' | 'reopened';

const verbs: Record<Action, string> = {
  confirmed: 'confirm',
  rejected: 'reject',
  reopened: 'reopen',
};

function notifyBulkResult(result: BulkResolutionResult, action: Action) {
  const { succeeded, failed, errors } = result;
  const total = succeeded + failed;

  if (failed === 0) {
    const message =
      total === 1 ? `Match ${action} successfully` : `${succeeded} matches ${action} successfully`;
    notifications.show({ title: 'Success', message, color: 'green' });
    return;
  }

  if (succeeded === 0) {
    const detail = errors.length > 0 ? errors.join('. ') : 'Unknown error';
    notifications.show({
      title: `Failed to ${verbs[action]} matches`,
      message: detail,
      color: 'red',
    });
    return;
  }

  notifications.show({
    title: 'Partial failure',
    message: `${succeeded} of ${total} ${action} successfully. ${failed} failed.`,
    color: 'amber',
  });
}

export default function MatchingSectionNav({ suggestions }: MatchingSectionNavProps) {
  const highConfidence = suggestions.filter(s => s.confidence === 'HIGH');
  const mediumConfidence = suggestions.filter(s => s.confidence === 'MEDIUM');
  const ambiguous = suggestions.filter(s => s.confidence === 'MANUAL_REVIEW');

  const highPendingCount = highConfidence.filter(s => s.status === 'PENDING').length;
  const mediumPendingCount = mediumConfidence.filter(s => s.status === 'PENDING').length;
  const ambiguousPendingCount = ambiguous.filter(s => s.status === 'PENDING').length;

  const confirmMatches = useConfirmMatches();
  const rejectMatches = useRejectMatches();
  const reopenMatches = useReopenMatches();
  const unlinkMatches = useUnlinkMatches();

  const actionHandlers = {
    onConfirm: (id: string) =>
      confirmMatches.mutate([id], {
        onSuccess: ({ data }) => notifyBulkResult(data, 'confirmed'),
      }),
    onReject: (id: string) =>
      rejectMatches.mutate([id], {
        onSuccess: ({ data }) => notifyBulkResult(data, 'rejected'),
      }),
    onUnlink: (id: string) =>
      unlinkMatches.mutate([id], {
        onSuccess: () =>
          notifications.show({
            title: 'Success',
            message: 'Match unlinked successfully',
            color: 'green',
          }),
      }),
    onReopen: (id: string) =>
      reopenMatches.mutate([id], {
        onSuccess: ({ data }) => notifyBulkResult(data, 'reopened'),
      }),
    onBulkConfirm: async (ids: string[]) => {
      await confirmMatches.mutateAsync(ids);
    },
    onBulkReject: async (ids: string[]) => {
      await rejectMatches.mutateAsync(ids);
    },
    onBulkUnlink: async (ids: string[]) => {
      await unlinkMatches.mutateAsync(ids);
    },
    onBulkReopen: async (ids: string[]) => {
      await reopenMatches.mutateAsync(ids);
    },
  };

  return (
    <Tabs orientation="vertical" variant="vertical" defaultValue="high">
      <Tabs.List>
        <Tabs.Tab value="high">High Confidence ({highPendingCount})</Tabs.Tab>
        <Tabs.Tab value="medium">Medium Confidence ({mediumPendingCount})</Tabs.Tab>
        <Tabs.Tab value="ambiguous">Ambiguous ({ambiguousPendingCount})</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="high" pl="lg">
        <SuggestionReviewTable
          suggestions={highConfidence}
          headerAction={
            highPendingCount > 0 && (
              <Button
                onClick={() =>
                  confirmMatches.mutate(
                    highConfidence.filter(s => s.status === 'PENDING').map(s => s.id),
                    { onSuccess: ({ data }) => notifyBulkResult(data, 'confirmed') },
                  )
                }
                loading={confirmMatches.isPending}
              >
                Confirm all suggested matches ({highPendingCount})
              </Button>
            )
          }
          {...actionHandlers}
        />
      </Tabs.Panel>

      <Tabs.Panel value="medium" pl="lg">
        <SuggestionReviewTable
          suggestions={mediumConfidence}
          headerAction={
            mediumPendingCount > 0 && (
              <Button
                onClick={() =>
                  confirmMatches.mutate(
                    mediumConfidence.filter(s => s.status === 'PENDING').map(s => s.id),
                    { onSuccess: ({ data }) => notifyBulkResult(data, 'confirmed') },
                  )
                }
                loading={confirmMatches.isPending}
              >
                Confirm all suggested matches ({mediumPendingCount})
              </Button>
            )
          }
          {...actionHandlers}
        />
      </Tabs.Panel>

      <Tabs.Panel value="ambiguous" pl="lg">
        <AmbiguousGroupCards
          suggestions={ambiguous}
          {...actionHandlers}
          onRejectGroup={ids =>
            rejectMatches.mutate(ids, {
              onSuccess: ({ data }) => notifyBulkResult(data, 'rejected'),
            })
          }
        />
      </Tabs.Panel>
    </Tabs>
  );
}
